import type { MessageDescriptor } from 'react-intl-next';

import { Recommendation, RecommendationDetails, RecommendationMetadata } from './api';
import {
	SETTING_KEYS,
	getSuggestionSettingArray,
	getRecommendationMatcherArray,
	defaultRecommendationCard,
} from './constants';
import type { SettingDisplayType } from './states';

/**
 * Type for the suggestion setting item
 */
export interface SuggestionSetting {
	Icon: React.ElementType;
	settingKey: keyof SettingDisplayType;
	title: MessageDescriptor;
	description?: MessageDescriptor;
	parentSetting?: SETTING_KEYS;
}
export interface SuggestionSettingView extends SuggestionSetting {
	defaultValue: boolean;
	display: boolean;
	indent: boolean;
}

export interface RecommendationCard {
	icon: React.ElementType;
	title: MessageDescriptor;
	description: MessageDescriptor;
}

export interface RecommendationMatcher extends RecommendationMetadata, RecommendationCard {
	settingKey: keyof SettingDisplayType;
}
/**
 * Checks if the item is visible based on its parent + grandparent + great grandparent setting.
 */
const isItemVisible = (item: SuggestionSetting, result: SuggestionSettingView[]): boolean => {
	const checkParentVisibility = (currentItem: SuggestionSetting): boolean => {
		const parentSettingKey = currentItem.parentSetting;
		if (!parentSettingKey) {
			return true; // No parent means this item is visible
		}
		const parentItem = result.find((i) => i.settingKey === parentSettingKey);
		if (!parentItem) {
			return false; // No parent found, consider it not visible
		}
		// Check the parent setting's default value
		const isParentEnabled = parentItem.defaultValue ?? false;
		// If the parent is not visible, this item is not visible
		if (!isParentEnabled) {
			return false;
		}
		// Recursively check the visibility of the parent
		return isParentEnabled && checkParentVisibility(parentItem);
	};

	// Check the visibility of the item considering its parent and grandparent settings
	return checkParentVisibility(item);
};

/**
 * Get the suggestion settings with their
 * default values
 * display and indent properties
 * in a correct order
 */
export const getSettingsWithValues = (settings: SettingDisplayType | null) => {
	const result: SuggestionSettingView[] = [];
	const processed = new Set();

	const suggestionSetting = getSuggestionSettingArray();

	const processSetting = (setting: SuggestionSetting) => {
		if (processed.has(setting.settingKey)) {
			return;
		}

		if (setting.parentSetting && !processed.has(setting.parentSetting)) {
			const parentSetting = suggestionSetting.find((s) => s.settingKey === setting.parentSetting);
			if (parentSetting) {
				processSetting(parentSetting);
			}
		}

		processed.add(setting.settingKey);
		result.push({
			...setting,
			defaultValue: settings?.[setting.settingKey] ?? true,
			display: isItemVisible(setting, result),
			settingKey: setting.settingKey,
			indent:
				setting.parentSetting && setting.parentSetting !== SETTING_KEYS.ALL_SUGGESTIONS
					? true
					: false,
		});
	};

	suggestionSetting.forEach(processSetting);

	return result;
};

/**
 * Type for the return setting object
 */
export type returnSettingType = {
	[key: string]: boolean;
};

/**
 * Get the new settings based on the setting key
 */
export const getNewSettings = (
	settings: SettingDisplayType | null,
	settingKey: keyof SettingDisplayType | null,
) => {
	const suggestionSettingWithValues = getSettingsWithValues(settings);

	const newSettings = suggestionSettingWithValues.reduce<returnSettingType>((suggestion, s) => {
		// Ignored via go/ees005
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		suggestion[s.settingKey] = s.defaultValue!;
		return suggestion;
	}, {});

	if (settingKey) {
		newSettings[settingKey] = !newSettings[settingKey];

		// toggle all child settings recursively
		const toggleChildSettings = (parentKey: keyof SettingDisplayType, newSettingValue: boolean) => {
			suggestionSettingWithValues.forEach((value) => {
				if (value.parentSetting === parentKey) {
					newSettings[value.settingKey] = newSettingValue;
					toggleChildSettings(value.settingKey, newSettingValue);
				}
			});
		};

		toggleChildSettings(settingKey, newSettings[settingKey]);
	}

	return newSettings;
};

/**
 * Find the recommendation matcher for the recommendation metadata
 */
const detailsMatch = (expected: RecommendationDetails, actual: RecommendationDetails): boolean => {
	return Object.entries(expected).every(([key, expectedValue]) => {
		if (expectedValue === undefined) {
			// If no expected value is provided, match any value.
			return true;
		}

		const actualValue = actual[key];
		if (actualValue === undefined) {
			return false;
		}

		if (Array.isArray(expectedValue)) {
			// Match if all actual values are included in the expected value.
			return Array.isArray(actualValue) && actualValue.every((v) => expectedValue.includes(v));
		}

		if (typeof expectedValue === 'object') {
			return (
				typeof actualValue === 'object' &&
				!Array.isArray(actualValue) &&
				actualValue !== null &&
				Object.entries(expectedValue).every(([k, v]) => actualValue[k] === v)
			);
		}

		return actualValue === expectedValue;
	});
};

/**
 * Finds the first matching recommendation based on the provided metadata and optional setting values.
 * If no matches are found, it returns an unknown recommendation matcher with the provided metadata.
 * If setting values are provided, it filters the matches to exclude those with a setting key that is explicitly set to `false`.
 */
const findMatcher = (
	meta: RecommendationMetadata,
	settingValues?: Record<string, boolean>,
): RecommendationMatcher | undefined => {
	const matches = getRecommendationMatcherArray().filter((i) => {
		const typeMatches = meta.type !== undefined && i.type === meta.type;

		if (typeMatches) {
			return !i.details || detailsMatch(i.details, meta.details || {});
		}

		const transformationTypeMatches =
			!!meta.transformAction && i.transformAction === meta.transformAction;
		return transformationTypeMatches;
	});

	if (matches.length === 0) {
		// If we don't have any matches, it is an unknown recommendation and we should let it through
		const { transformAction, type, details } = meta;
		return {
			transformAction,
			type,
			details,
			settingKey: SETTING_KEYS.UNKNOWN,
			...defaultRecommendationCard,
		};
	}

	if (!settingValues) {
		return matches[0];
	}

	return matches.filter((i) => settingValues[i.settingKey] !== false)[0];
};

/**
 * Set filtered flag in the recommendations according to the settings
 */
export const syncFilteredRecommendations = (
	recommendations: Recommendation[],
	settings: SettingDisplayType,
): Recommendation[] =>
	recommendations.map((meta) => ({
		...meta,
		filtered: !findMatcher(meta, settings),
	}));

/**
 * Filter recommendations according to the settings
 */
export const filterRecommendations = (
	recommendations: Recommendation[],
	settings: SettingDisplayType,
): Recommendation[] => recommendations.filter((meta) => !!findMatcher(meta, settings));

/**
 * Get recommendation card according to the recommendation metadata
 */
export const getRecommendationCard = (meta: RecommendationMetadata): RecommendationCard => {
	const match = findMatcher(meta);
	return match
		? {
				title: match.title,
				description: match.description,
				icon: match.icon,
			}
		: defaultRecommendationCard;
};
